import { useGetRenewVisaBasicInformationDocumentGroupInfo, useGetRenewVisaDocumentGroupInfo } from "@/api/services/main/renew-visa";
import { IconTriangleDown } from "@/assets/icons/IconTriangleDown";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { RenewVisaAfterSubmitApplicationResultStep } from "@/features/renew-visa/components/present/RenewVisaAfterSubmitApplicationResultStep";
import { RenewVisaAfterSubmitApplicationStep } from "@/features/renew-visa/components/present/RenewVisaAfterSubmitApplicationStep";
import { RenewVisaApplicationStep } from "@/features/renew-visa/components/present/RenewVisaApplicationStep";
import { RenewVisaBasicInfoStep } from "@/features/renew-visa/components/present/RenewVisaBasicInfoStep";
import { RenewVisaOnlineSubmitApplicationStep } from "@/features/renew-visa/components/present/RenewVisaOnlineSubmitApplicationStep";
import { RenewVisaProgressStep } from "@/features/renew-visa/components/present/RenewVisaProgressStep";
import { RenewVisaResidenceCardInfoStep } from "@/features/renew-visa/components/present/RenewVisaResidenceCardInfoStep";
import { RenewVisaUpdateDocumentNoteStep } from "@/features/renew-visa/components/present/RenewVisaUpdateDocumentNoteStep";
import { RENEW_VISA_LIST_TAB } from "@/features/renew-visa/constants/renewVisaListTab";
import { findRenewVisaDocumentStepGroup } from "@/features/renew-visa/utils/findDocumentStepGroup";
import { AppRoutes } from "@/routes/config";
import { background, divider, text } from "@/theme/colors";
import { DOCUMENT_STEP_KEYS, RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { toNumber } from "@/utils/number";
import { getFullName, truncateWithEllipsis } from "@/utils/string";
import { Box, Stack } from "@mui/material";
import { useMemo } from "react";

export const RenewVisaHistoryDetailPage = () => {
  const { data: renewVisaDocumentGroup, isLoading: isLoadingRenewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const basicInfoStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_BASIC_INFORMATION);

  const applicationStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_CREATE_DOCUMENT);
  const onlineSubmitApplicationStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_SUBMIT);
  const afterSubmitApplicationStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION,
  );
  const afterSubmitApplicationResultStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION_RESULT,
  );
  const residenceCardInfoStepGroupData = findRenewVisaDocumentStepGroup(
    renewVisaDocumentGroup,
    DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_UPDATE_RESIDENCE_CARD,
  );

  const { data: basicInfoData, isLoading: isLoadingBasicInfo } = useGetRenewVisaBasicInformationDocumentGroupInfo({
    renewVisaDocumentStepGroupId: toNumber(basicInfoStepGroupData?.id),
  });

  const isSkipStep = useMemo(() => !!renewVisaDocumentGroup && renewVisaDocumentGroup.isSkipStep, [renewVisaDocumentGroup]);

  const title = useMemo(() => {
    if (!renewVisaDocumentGroup) return "";
    const firstEmployeeFullName = getFullName({
      lastName: renewVisaDocumentGroup?.employeeRenewVisaDocuments.at(0)?.employee.lastName,
      firstName: renewVisaDocumentGroup?.employeeRenewVisaDocuments.at(0)?.employee.firstName || "",
      middleName: renewVisaDocumentGroup?.employeeRenewVisaDocuments.at(0)?.employee.middleName,
    });
    return `${renewVisaDocumentGroup?.company.companyName}│${truncateWithEllipsis(firstEmployeeFullName, 26)}さん${renewVisaDocumentGroup?.employeeRenewVisaDocuments.length - 1 > 0 ? `と他${renewVisaDocumentGroup?.employeeRenewVisaDocuments.length - 1}名` : ""}の在留期間更新`;
  }, [renewVisaDocumentGroup]);

  const isLoading = useMemo(() => isLoadingRenewVisaDocumentGroup || isLoadingBasicInfo, [isLoadingRenewVisaDocumentGroup, isLoadingBasicInfo]);

  if (isLoading) return <LoadingPage />;

  return (
    <>
      <TopPage title={title} backUrl={`${AppRoutes.renewVisa}?tab=${RENEW_VISA_LIST_TAB.COMPLETE_AND_WITHDRAWN_AND_REJECTED}`} />
      <RenewVisaProgressStep />
      <Box p="24px" border={`1px solid ${divider.middle}`} borderRadius={2} bgcolor={background.white}>
        {!!basicInfoData && (
          <>
            {basicInfoStepGroupData && <RenewVisaBasicInfoStep isSkipStep={isSkipStep} stepGroupData={basicInfoStepGroupData} readonly />}
            <Stack alignItems="center" my={3}>
              <IconTriangleDown color={text.disable} />
            </Stack>
          </>
        )}
        {applicationStepGroupData && (
          <RenewVisaApplicationStep
            isExistBasicInfoData={!!basicInfoData}
            isSkipStep={isSkipStep}
            stepGroupData={applicationStepGroupData}
            readonly
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {onlineSubmitApplicationStepGroupData && (
          <RenewVisaOnlineSubmitApplicationStep
            isExistBasicInfoData={!!basicInfoData}
            isSkipStep={isSkipStep}
            stepGroupData={onlineSubmitApplicationStepGroupData}
            readonly
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {afterSubmitApplicationStepGroupData && (
          <RenewVisaAfterSubmitApplicationStep isExistBasicInfoData={!!basicInfoData} stepGroupData={afterSubmitApplicationStepGroupData} readonly />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {afterSubmitApplicationResultStepGroupData && (
          <RenewVisaAfterSubmitApplicationResultStep
            isExistBasicInfoData={!!basicInfoData}
            stepGroupData={afterSubmitApplicationResultStepGroupData}
            readonly
          />
        )}
        <Stack alignItems="center" my={3}>
          <IconTriangleDown color={text.disable} />
        </Stack>
        {residenceCardInfoStepGroupData && (
          <RenewVisaResidenceCardInfoStep
            isExistBasicInfoData={!!basicInfoData}
            isFinishAfterSubmitApplicationResultStep={afterSubmitApplicationResultStepGroupData?.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
            stepGroupData={residenceCardInfoStepGroupData}
            readonly
          />
        )}
        <RenewVisaUpdateDocumentNoteStep readonly />
      </Box>
    </>
  );
};
